import React, { FC, useEffect, useMemo, useState } from 'react';
import { Button, Chip, Theme, Typography, useTheme } from '@mui/material';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import { useSelector } from 'react-redux';

import { ICreateAbsenceRequestDTO, IEmployeeBalanceDTO, IHolidayDTO } from 'src/api/dtos';
import { AbsenceDateRange } from 'src/features/absences/future-balance';
import { ArrowLeftIcon, CalendarIcon, Drawer, Modal } from 'src/shared/components';
import { ModalFooterProps } from 'src/shared/components/Modal/ModalFooter';
import { ModalHeaderProps } from 'src/shared/components/Modal/ModalHeader';
import { useAppDispatch, usePrevious } from 'src/shared/hooks';
import { TranslationFunction, useTranslate } from 'src/shared/providers';
import { isAbsenceRequestInvalid } from 'src/shared/utils';
import { formatBoldText } from 'src/shared/utils/sanitizeString';
import { resetNewAbsenceItem } from 'src/store/absence/absences.actions';
import { RootState } from 'src/store/types';
import { getTimeFormat } from 'src/store/user/user.selectors';

import { AbsenceBalanceRequest } from './AbsenceBalanceRequest';
import { AbsenceRequestForm } from './AbsenceRequestForm';
import { AbsenceRequestSuccess } from './AbsenceRequestSuccess';
import {
  useAbsenceRequestSubmit,
  useAbsenceRequestValidation,
  useBalanceCheck,
  useScheduleStatusValidation,
} from './hooks';

interface Props {
  open: boolean;
  onClose: () => void;
  cancelling: boolean;
  onCancelling: (notificationId: number) => void;
  onViewDetails?: () => void;
}

export const AbsenceRequestDrawer: FC<Props> = ({ open, cancelling, onClose, onCancelling, onViewDetails }) => {
  const { t } = useTranslate();
  const theme = useTheme();
  const timeFormat = useSelector(getTimeFormat);
  const dispatch = useAppDispatch();

  const [steps, setSteps] = useState<'request' | 'success' | 'balance'>('request');
  const [inputs, setInputs] = useState<Partial<ICreateAbsenceRequestDTO>>({});
  const [openModal, setOpenModal] = useState<IHolidayDTO | undefined>();
  const previousInputs = usePrevious(inputs);

  const { submitting } = useSelector((state: RootState) => state.absences);

  const {
    balance,
    checkBalance,
    loading: loadingBalance,
    cancel: cancelBalance,
    reset: resetBalance,
    setBalance,
  } = useBalanceCheck({ inputs });

  const { validate, hasErrors, resetErrors } = useAbsenceRequestValidation({ inputs });

  const { validateStatus, resetWarning, warnings, inputTargetKey } = useScheduleStatusValidation({
    inputs: pick(inputs, ['isPartialDay', 'startDate', 'startTime', 'endTime']),
  });

  const { submitForm } = useAbsenceRequestSubmit({
    inputs,
    onOpenModal: setOpenModal,
    onSuccess: () => setSteps('success'),
    onBalanceUpdate: setBalance,
  });

  const reset = (newIsPartialDay?: boolean) => {
    setInputs({ isPartialDay: newIsPartialDay });
    resetErrors();
    resetWarning();
    dispatch(resetNewAbsenceItem());
    setSteps('request');
  };

  const handleBalanceCancellation = () => {
    cancelBalance();
  };

  const handleUpdate = (newInputs: Partial<ICreateAbsenceRequestDTO>) => {
    setInputs({ ...inputs, ...newInputs });
  };

  const handleCheck = () => {
    reset(!inputs.isPartialDay);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const drawerProps = useMemo(
    () =>
      getDrawerPropsByStep({
        steps,
        inputs,
        balance,
        t,
        timeFormat,
        theme,
        onRequestNavigate: () => setSteps('request'),
      }),
    [steps, theme],
  );

  useEffect(() => {
    (async () => {
      await validateStatus();

      const previousRequiredFields = omit(previousInputs, ['notes']);
      const requiredFields = omit(inputs, ['notes']);

      if (isEqual(previousRequiredFields, requiredFields) || isAbsenceRequestInvalid(inputs)) {
        return;
      }

      if (balance) {
        resetBalance();
      }

      await validate();
    })();
  }, [inputs]);

  const handleCancel = () => {
    setOpenModal(undefined);
  };

  const handleSubmit = () => {
    submitForm(true);
  };

  const modalHeaderProps: ModalHeaderProps = {
    title: t('absences.holidayOverlap'),
    onClose: handleCancel,
  };

  const modalFooterProps: ModalFooterProps = {
    submitText: t('global.confirm'),
    loading: submitting,
    onSubmit: handleSubmit,
    closeText: t('global.cancel'),
    disabled: submitting,
    onClose: handleCancel,
  };

  return (
    <Drawer open={open} onClose={handleClose} {...drawerProps}>
      {steps === 'request' && (
        <AbsenceRequestForm
          inputs={inputs}
          inputTargetKey={inputTargetKey}
          hasErrors={hasErrors}
          scheduleWarnings={warnings}
          balance={balance}
          loadingBalance={loadingBalance}
          onBalanceCheck={checkBalance}
          onBalanceCancel={handleBalanceCancellation}
          onUpdate={handleUpdate}
          onCheck={handleCheck}
          onSubmit={submitForm}
          onNavigate={() => setSteps('balance')}
        />
      )}
      {steps === 'success' && (
        <AbsenceRequestSuccess
          balance={balance}
          cancelling={cancelling}
          onNavigate={() => reset()}
          onViewDetails={onViewDetails}
          onCancelling={onCancelling}
        />
      )}
      {steps === 'balance' && <AbsenceBalanceRequest inputs={inputs} balance={balance} />}
      <Modal
        sx={{ width: 550 }}
        open={!!openModal}
        onClose={handleCancel}
        modalHeaderProps={modalHeaderProps}
        modalFooterProps={modalFooterProps}
      >
        <Typography>
          {formatBoldText(t('absences.yourSelectedDatesOverlapWithNameDoYouWant', { name: openModal?.description }))}
        </Typography>
      </Modal>
    </Drawer>
  );
};

const getDrawerPropsByStep = ({
  steps,
  inputs,
  balance,
  t,
  timeFormat,
  theme,
  onRequestNavigate,
}: {
  steps: 'request' | 'success' | 'balance';
  inputs: Partial<ICreateAbsenceRequestDTO>;
  balance: IEmployeeBalanceDTO | undefined;
  t: TranslationFunction;
  timeFormat: string | undefined;
  theme: Theme;
  onRequestNavigate: () => void;
}) => {
  switch (steps) {
    case 'balance':
      return {
        title: t('absences.viewBreakdown'),
        description: (
          <Chip
            className={theme.palette.mode === 'light' ? 'text-black' : 'text-white'}
            variant="outlined"
            color="primary"
            label={
              <div className="fw-500 d-flex align-items-center">
                <CalendarIcon size={18} className="me-2" /> {balance?.benefitName}:
                <AbsenceDateRange absenceRequest={inputs} timeFormat={timeFormat} />
              </div>
            }
          />
        ),
        headerElement: (
          <Button
            id="back-to-absence-request"
            className="mb-3"
            variant="contained"
            color="info"
            startIcon={<ArrowLeftIcon />}
            onClick={onRequestNavigate}
          >
            {t('absences.backToAbsenceRequest')}
          </Button>
        ),
      };
    case 'success':
      return {
        title: t('absences.success'),
      };
    case 'request':
    default:
      return {
        title: t('absences.requestAnAbsence'),
        description: t('absences.youWillBeAbleToSubmitAbsenceRequestsHere'),
      };
  }
};
